import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContentCard from '@/components/feature/ContentCard';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'dianshiju');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || ''} - 国产精品在线`,
    description: currentCategory?.description || '',
    url: `https://www.guochangjingpin.com/category/${activeSlug}`,
  };

  return (
    <>
      <SEOHead
        title={currentCategory?.name || '分类'}
        description={currentCategory?.description || '浏览国产精品在线的海量内容分类'}
        keywords={`${currentCategory?.name},国产精品在线,国产${currentCategory?.name}在线观看`}
        canonicalUrl={`https://www.guochangjingpin.com/category/${activeSlug}`}
        schemaData={webPageSchema}
      />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: currentCategory?.name || '分类' },
          ]}
        />

        {/* Category Header */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex items-center gap-4 mb-6">
            {currentCategory && (
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${currentCategory.icon} text-2xl`}></i>
              </div>
            )}
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {currentCategory?.name}
              </h1>
              <p className="mt-1 text-sm text-foreground-600">{currentCategory?.description}</p>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-foreground-800'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Article Grid */}
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categoryArticles.map((article) => (
                <ContentCard
                  key={article.id}
                  id={article.id}
                  title={article.title}
                  category={article.category}
                  excerpt={article.excerpt}
                  imageUrl={article.imageUrl}
                  publishDate={article.publishDate}
                  slug={article.slug}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500 text-sm">该分类暂无内容，请浏览其他分类</p>
            </div>
          )}
        </section>
      </main>
    </>
  );
}